import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from '../components/LoadingButton';
import BookScanner from '../components/BookScanner';
import ScannedBooksList, { ScannedBook } from '../components/ScannedBooksList';
import EditScannedBookModal from '../components/EditScannedBookModal';
import axios from 'axios';
import { Search, BookOpen, Plus, ArrowLeft, Bookmark, Calendar, Hash, X, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';

interface BookSearchResult {
  google_id: string;
  title: string;
  author: string;
  isbn: string;
  published_date: string;
  description: string;
  cover_image: string;
}

const AddBook: React.FC = () => {
  const { showSuccess, showError, showBook } = useToast();
  const { isLoading, withLoading } = useLoading();
  const [activeTab, setActiveTab] = useState<'search' | 'manual' | 'scanner'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BookSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookSearchResult | null>(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isSelectingBook, setIsSelectingBook] = useState(false);
  const [displayQuery, setDisplayQuery] = useState('');
  
  // Manual book form state
  const [manualBook, setManualBook] = useState({
    title: '',
    author: '',
    isbn: '',
    published_date: '',
    description: '',
    cover_image: ''
  });
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');
  
  // Scanner states
  const [scannedBooks, setScannedBooks] = useState<ScannedBook[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [editingBook, setEditingBook] = useState<ScannedBook | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const navigate = useNavigate();

  // Debounced search with improved timing
  useEffect(() => {
    if (isSelectingBook) {
      setIsSelectingBook(false);
      return;
    }

    if (searchQuery.trim().length >= 3) {
      setIsWaiting(true);
    } else {
      setIsWaiting(false);
      setSearchResults([]);
    }

    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 3) {
        searchBooks(searchQuery);
      } else {
        setSearchResults([]);
      }
      setIsWaiting(false);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isSelectingBook]);

  const searchBooks = withLoading('search-books', async (query: string) => {
    try {
      const response = await axios.get(`/api/books/search?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error: any) {
      showError('Erreur', 'Erreur lors de la recherche de livres');
      console.error('Search error:', error);
    }
  });

  const addBookToLibrary = withLoading('add-book', async (book: BookSearchResult) => {
    try {
      await axios.post('/api/books/add', book);
      showBook('Livre ajouté', 'Le livre a été ajouté à votre bibliothèque');
      setSelectedBook(null);
      setSearchQuery('');
      setSearchResults([]);
      
      setTimeout(() => {
        navigate('/library');
      }, 2000);
    } catch (error: any) {
      showError('Erreur', error.response?.data?.error || 'Erreur lors de l\'ajout du livre');
    }
  });

  // Handle cover image upload
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image to server and get URL
  const uploadCoverImage = async (): Promise<string> => {
    if (!coverImageFile) return '';
    
    const formData = new FormData();
    formData.append('cover_image', coverImageFile);
    
    const response = await axios.post('/api/books/upload-cover', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data.imageUrl;
  };

  // Add manual book to library
  const addManualBookToLibrary = withLoading('add-manual-book', async () => {
    if (!manualBook.title || !manualBook.author) {
      showError('Erreur', 'Le titre et l\'auteur sont obligatoires');
      return;
    }
    
    try {
      let coverImageUrl = manualBook.cover_image;
      
      // Upload cover image if provided
      if (coverImageFile) {
        coverImageUrl = await uploadCoverImage();
      }
      
      const bookData = {
        ...manualBook,
        cover_image: coverImageUrl,
        google_id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` // Generate unique ID for manual books
      };
      
      await axios.post('/api/books/add', bookData);
      showBook('Livre ajouté', 'Le livre a été ajouté à votre bibliothèque');
      
      // Reset form
      setManualBook({
        title: '',
        author: '',
        isbn: '',
        published_date: '',
        description: '',
        cover_image: ''
      });
      setCoverImageFile(null);
      setCoverImagePreview('');
      
      setTimeout(() => {
        navigate('/library');
      }, 2000);
    } catch (error: any) {
      showError('Erreur', error.response?.data?.error || 'Erreur lors de l\'ajout du livre');
    }
  });

  const handleBookSelect = (book: BookSearchResult) => {
    setSelectedBook(book);
    setSearchResults([]);
    setDisplayQuery(book.title);
    setSearchQuery('');
  };

  const formatPublicationDate = (dateString: string) => {
    if (!dateString) return 'Date inconnue';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date inconnue';
      }
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  // Scanner functions
  const handleImagesScanned = (detectedBooks: Array<{title: string, author: string}>) => {
    const newScannedBooks: ScannedBook[] = detectedBooks.map((book, index) => ({
      id: `scanned_${Date.now()}_${index}`,
      title: book.title,
      author: book.author,
      enriched: false,
      enrichmentStatus: 'pending' as const,
      selected: true // Auto-select all detected books
    }));
    
    setScannedBooks(prev => [...prev, ...newScannedBooks]);
  };

  const handleScanningStart = () => {
    setIsScanning(true);
  };

  const handleScanningComplete = () => {
    setIsScanning(false);
  };

  const handleBooksChange = (books: ScannedBook[]) => {
    setScannedBooks(books);
  };

  const handleEditBook = (book: ScannedBook) => {
    setEditingBook(book);
    setIsEditModalOpen(true);
  };

  const handleSaveEditedBook = (editedBook: ScannedBook) => {
    setScannedBooks(prev => 
      prev.map(book => book.id === editedBook.id ? editedBook : book)
    );
    setIsEditModalOpen(false);
    setEditingBook(null);
  };

  const handleAddToLibrary = withLoading('add-batch-books', async (selectedBooks: ScannedBook[]) => {
    if (selectedBooks.length === 0) {
      showError('Erreur', 'Veuillez sélectionner au moins un livre');
      return;
    }

    try {
      // Prepare books data for API
      const booksData = selectedBooks.map(book => {
        if (book.enriched && book.googleData) {
          return {
            google_id: book.googleData.google_id,
            title: book.title,
            author: book.author,
            isbn: book.googleData.isbn,
            published_date: book.googleData.published_date,
            description: book.googleData.description,
            cover_image: book.googleData.cover_image
          };
        } else {
          // Manual book data
          return {
            google_id: book.googleData?.google_id || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            title: book.title,
            author: book.author,
            isbn: book.googleData?.isbn || 'N/A',
            published_date: book.googleData?.published_date || 'N/A',
            description: book.googleData?.description || 'Aucune description disponible',
            cover_image: book.googleData?.cover_image || ''
          };
        }
      });

      const response = await axios.post('/api/books/add-batch', {
        books: booksData
      });

      if (response.data.summary.success > 0) {
        showBook('Livres ajoutés', `${response.data.summary.success} livre(s) ajouté(s) à votre bibliothèque`);
        
        // Remove added books from scanned list
        const addedBookIds = response.data.results.map((r: any) => r.book_id);
        setScannedBooks(prev => prev.filter(book => 
          !selectedBooks.some(selected => selected.id === book.id)
        ));
        
        setTimeout(() => {
          navigate('/library');
        }, 2000);
      }

      if (response.data.summary.failed > 0) {
        showError('Attention', `${response.data.summary.failed} livre(s) n'ont pas pu être ajoutés`);
      }

    } catch (error: any) {
      showError('Erreur', error.response?.data?.error || 'Erreur lors de l\'ajout des livres');
    }
  });

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/library"
              className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </Link>
            <div className="flex-1">
              <h1 className="font-serif text-2xl text-ink">
                Ajouter un livre
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('search')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-forest-600 text-forest-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Search className="h-4 w-4 inline mr-2" />
              Rechercher un livre
            </button>
            <button
              onClick={() => setActiveTab('manual')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'manual'
                  ? 'border-forest-600 text-forest-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Ajouter manuellement
            </button>
            <button
              onClick={() => setActiveTab('scanner')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'scanner'
                  ? 'border-forest-600 text-forest-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Camera className="h-4 w-4 inline mr-2" />
              Scanner des livres
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Search Section */}
        {activeTab === 'search' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-8 mb-6 sm:mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
              <Search className="h-5 w-5 mr-2 text-forest-700" />
              Rechercher un livre
            </h2>
            <p className="text-gray-600">
              Tapez le titre, l'auteur ou l'ISBN pour trouver votre livre
            </p>
          </div>
          
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={selectedBook ? displayQuery : searchQuery}
              onChange={(e) => {
                if (selectedBook) {
                  setSelectedBook(null);
                  setDisplayQuery('');
                  setSearchQuery(e.target.value);
                } else {
                  setSearchQuery(e.target.value);
                }
              }}
              className={`w-full pl-12 pr-4 py-3 sm:py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600 transition-colors text-sm sm:text-base ${
                isWaiting ? 'border-yellow-300 bg-yellow-50' : ''
              }`}
              placeholder="Ex: Le Petit Prince, Antoine de Saint-Exupéry, 9782070612758..."
              disabled={loading}
            />
            {isWaiting && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <div className="animate-pulse w-4 h-4 bg-yellow-400 rounded-full"></div>
              </div>
            )}
          </div>
          
          {isWaiting && !loading && (
            <div className="mt-3 text-sm text-gray-500 flex items-center">
              <div className="animate-pulse w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
              Recherche en attente...
            </div>
          )}
          
          {loading && (
            <div className="mt-3 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-stone-200 border-t-forest-600 mr-2"></div>
              Recherche en cours...
            </div>
          )}
        </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && !selectedBook && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-8 mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-forest-700" />
              Résultats de recherche ({searchResults.length})
            </h3>
            <div className="grid gap-3 sm:gap-4">
              {searchResults.map((book) => (
                <div
                  key={book.google_id}
                  className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 cursor-pointer transition-all duration-200"
                  onClick={() => handleBookSelect(book)}
                >
                  <div className="flex-shrink-0">
                    {book.cover_image ? (
                      <img
                        src={book.cover_image}
                        alt={book.title}
                        className="w-12 h-16 sm:w-16 sm:h-20 object-cover rounded-lg shadow-sm"
                      />
                    ) : (
                      <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                        <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                      {book.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-1">
                      par <span className="font-medium">{book.author}</span>
                    </p>
                    {book.published_date && (
                      <p className="text-xs text-gray-500 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(book.published_date).getFullYear()}
                      </p>
                    )}
                  </div>
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5 text-forest-700 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Book Preview */}
        {selectedBook && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <Bookmark className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-green-600" />
              Livre sélectionné
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              {/* Book Cover */}
              <div className="md:col-span-1">
                <div className="text-center">
                  {selectedBook.cover_image ? (
                    <img
                      src={selectedBook.cover_image}
                      alt={selectedBook.title}
                      className="w-full max-w-xs mx-auto rounded-lg shadow-md"
                    />
                  ) : (
                    <div className="w-full max-w-xs mx-auto h-96 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <BookOpen className="h-24 w-24 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Book Details */}
              <div className="md:col-span-2">
                <div className="mb-4 sm:mb-6">
                  <h1 className="font-serif text-xl sm:text-2xl lg:text-3xl text-ink mb-3">
                    {selectedBook.title}
                  </h1>
                  <p className="text-base sm:text-lg text-gray-600 mb-4">
                    par <span className="font-semibold">{selectedBook.author}</span>
                  </p>
                </div>

                {/* Book Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  {selectedBook.isbn && (
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-forest-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Hash className="h-5 w-5 text-forest-700" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">ISBN</h3>
                        <p className="text-gray-900 font-mono">{selectedBook.isbn}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedBook.published_date && (
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Date de publication</h3>
                        <p className="text-gray-900">{formatPublicationDate(selectedBook.published_date)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                {selectedBook.description && (
                  <div className="mb-8">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                    <p className="text-gray-700 leading-relaxed">
                      {selectedBook.description.length > 300 
                        ? `${selectedBook.description.substring(0, 300)}...` 
                        : selectedBook.description
                      }
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <LoadingButton
                    onClick={() => addBookToLibrary(selectedBook)}
                    loading={isLoading('add-book')}
                    loadingText="Ajout en cours..."
                    className="flex-1"
                    variant="primary"
                    size="lg"
                  >
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    <span className="hidden sm:inline">Ajouter à ma bibliothèque</span>
                    <span className="sm:hidden">Ajouter</span>
                  </LoadingButton>
                  
                  <button
                    onClick={() => {
                      setSelectedBook(null);
                      setDisplayQuery('');
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 text-sm sm:text-base"
                  >
                    <span className="hidden sm:inline">Choisir un autre livre</span>
                    <span className="sm:hidden">Autre livre</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedBook && searchQuery.trim().length >= 3 && searchResults.length === 0 && !loading && (
          <div className="text-center py-12 sm:py-16">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 sm:p-12 max-w-md mx-auto">
              <BookOpen className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4 sm:mb-6" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Aucun livre trouvé
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Essayez avec un autre titre, auteur ou ISBN.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-forest-600 text-white font-semibold rounded-lg hover:bg-forest-700 transition-all duration-200 text-sm sm:text-base"
              >
                Nouvelle recherche
              </button>
            </div>
          </div>
        )}

        {/* Manual Book Form */}
        {activeTab === 'manual' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                <Plus className="h-5 w-5 mr-2 text-forest-700" />
                Ajouter un livre manuellement
              </h2>
              <p className="text-gray-600">
                Remplissez les informations de votre livre
              </p>
            </div>

            <div className="space-y-6">
              {/* Title and Author */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du livre *
                  </label>
                  <input
                    type="text"
                    value={manualBook.title}
                    onChange={(e) => setManualBook({...manualBook, title: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                    placeholder="Ex: Le Petit Prince"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auteur *
                  </label>
                  <input
                    type="text"
                    value={manualBook.author}
                    onChange={(e) => setManualBook({...manualBook, author: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                    placeholder="Ex: Antoine de Saint-Exupéry"
                    required
                  />
                </div>
              </div>

              {/* ISBN and Published Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={manualBook.isbn}
                    onChange={(e) => setManualBook({...manualBook, isbn: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                    placeholder="Ex: 978-2-07-036822-8"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de publication
                  </label>
                  <input
                    type="date"
                    value={manualBook.published_date}
                    onChange={(e) => setManualBook({...manualBook, published_date: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={manualBook.description}
                  onChange={(e) => setManualBook({...manualBook, description: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  placeholder="Résumé du livre..."
                />
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image de couverture
                </label>
                <div className="space-y-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  />
                  {coverImagePreview && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Aperçu :</p>
                      <img
                        src={coverImagePreview}
                        alt="Aperçu de la couverture"
                        className="w-32 h-48 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* URL de couverture alternative */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de couverture (alternative)
                </label>
                <input
                  type="url"
                  value={manualBook.cover_image}
                  onChange={(e) => setManualBook({...manualBook, cover_image: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  placeholder="https://example.com/cover.jpg"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Utilisez cette option si vous ne souhaitez pas télécharger une image
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <LoadingButton
                  onClick={addManualBookToLibrary}
                  loading={isLoading('add-manual-book')}
                  loadingText="Ajout en cours..."
                  disabled={!manualBook.title || !manualBook.author}
                  className="w-full"
                  variant="primary"
                  size="lg"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Ajouter le livre
                </LoadingButton>
              </div>
            </div>
          </div>
        )}

        {/* Scanner Section */}
        {activeTab === 'scanner' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                  <Camera className="h-5 w-5 mr-2 text-forest-700" />
                  Scanner des livres
                </h2>
                <p className="text-gray-600">
                  Prenez des photos de vos livres ou de votre bibliothèque pour les ajouter automatiquement
                </p>
              </div>

              <BookScanner
                onImagesScanned={handleImagesScanned}
                onScanningStart={handleScanningStart}
                onScanningComplete={handleScanningComplete}
              />
            </div>

            {/* Scanned Books List */}
            {scannedBooks.length > 0 && (
              <ScannedBooksList
                books={scannedBooks}
                onBooksChange={handleBooksChange}
                onEditBook={handleEditBook}
                onAddToLibrary={handleAddToLibrary}
              />
            )}

            {/* Edit Modal */}
            <EditScannedBookModal
              book={editingBook}
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setEditingBook(null);
              }}
              onSave={handleSaveEditedBook}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddBook; 